import axios from 'axios'
import { motion } from 'framer-motion'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import Table from '../../components/Display/Table'
import Modal from '../../components/Display/Modal'
import NewEmployeeForm from './NewEmployeeForm'
import useSnackBars from '../../hooks/SnackbarConsumerHook'
import { useLocalStorageState } from '../../hooks/useLocalStorageState'
import { ColumnItemWrapper } from '../../components/General/ColumnItemWrapper'
import { ColumnsTable } from 'safe'
import FloatingMenu from '../../components/Display/FloatingMenu'
import { HiDotsVertical } from 'react-icons/hi'
import ModalConfirm from '../../components/Display/ModalConfirm'
import EditBenefitDeduction from './EditBenefitDeduction'
import { HeaderProps } from '../../components/Layout/Header'
import { DateTime } from 'luxon'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'
import { ThemeContext } from '../../context/ThemeContext'
import { MdPeople } from 'react-icons/md'

interface Employee {
  id: number
  name: string
  address?: string
  document?: string
  email: string
  phone?: string
  hireDate: string // Date in ISO format
  baseSalary: number
  salaryFreq: string
  birthDate?: string // Date in ISO format
  bankAccountNumber?: string
  bankCode?: string
  isActive: boolean
  jobPosition: string
  benefits?: {
    id: number
    description: string
    amount: number
  }[]
  deductions?: {
    id: number
    description: string
    amount: number
  }[]
  payments?: {
    id: number // payroll ID
    name: string // payroll name
    amount: number // amount paid
    date: string // Date in ISO format
  }[]
}

const server = process.env.REACT_APP_SERVER_URL

const Employee: React.FC<IEmployee> = ({ setHeaderActions }) => {
  const { addAlert } = useSnackBars()
  const { theme } = useContext(ThemeContext)

  const [employees, setEmployees] = useState<Employee[]>([])

  const [loading, setLoading] = useState<boolean>(false)

  const [numberOfPages, setNumberOfPages] = useState<number>(0)

  const [showModal, setShowModal] = useState<boolean>(false)

  const [searchParams, setSearchParams] = useState<string[]>([])
  const [fastSearch, setFastSearch] = useState<string>('')

  const [currentPage, setCurrentPage] = useState<number>(0)

  const [userConfig] = useLocalStorageState('user-config')

  const [menuVisible, setMenuVisible] = useState<number>(0)

  const [selectedEmployee, setSelectedEmployee] = useState<any | null>(null)

  const [selectedType, setSelectedType] = useState<'NEW' | 'EDIT'>('NEW')

  const [selectedEmployeeToDelete, setSelectedEmployeeToDelete] = useState<
    number | null
  >(null)

  const [selectedBenefitDeduction, setSelectedBenefitDeduction] = useState<
    any | null
  >(null)

  const user = useSelector((state: RootState) => state.auth.user)
  const userRole = user?.['custom:user_role']
  const userPlan = user?.['custom:user_plan']
  const isNonCorporateUser = userPlan === 'pyme' || userPlan === 'emprendedor' || userPlan === 'estudiantil'

  const parseToDate = useCallback(
    (dateString) => {
      const parts = dateString.split('-')
      const year = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10) - 1 // Months are 0-based in JavaScript
      const day = parseInt(parts[2], 10)
      return new Date(year, month, day)
    },
    [selectedEmployee]
  )

  const onGetEmployees = async (
    params: string[] = searchParams,
    pageNumber: number = 0,
    fastParam = fastSearch
  ): Promise<void> => {
    setFastSearch(fastParam)

    setCurrentPage(pageNumber)

    setSearchParams(params)

    setLoading(true)

    let newParams = [...params]

    if (fastParam) {
      newParams.push(`search=${fastParam}`)
    }

    let response = await axios.get<{
      employees: Employee[]
      numberOfPages: number
    }>(
      `${server}/employee?${newParams.join('&')}&pageNumber=${
        currentPage + 1
      }&pageSize=${userConfig.rowsPerPage}`
    )
    setEmployees(response.data.employees)
    setNumberOfPages(response.data?.numberOfPages || 0)
    setLoading(false)
  }

  useEffect(() => {
    // No mostrar acciones si no es plan corporativo
    if (isNonCorporateUser) {
      setHeaderActions({})
    } else {
      if (userRole !== 'seniat') {
        setHeaderActions({
          addAction: () => {
            setSelectedType('NEW')
            setShowModal(true)
        },
        advancedSearch: async (value) => {
          /** Por si se desea agregar un menu contextual en el filtro de busqueda */
          // if (typeof value === 'boolean') {
          //   setSearchModal(value)
          //   return
          // }
            onGetEmployees(searchParams, 0, value)
          },
        })
      }
    }

    return () => {
      setHeaderActions(null)
    }
  }, [setHeaderActions, isNonCorporateUser])

  useEffect(() => {
    // No cargar datos si no es plan corporativo
    if (!isNonCorporateUser) {
      onGetEmployees()
    }
  }, [isNonCorporateUser])

  const columns: ColumnsTable[] = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Documento',
      dataIndex: 'document',
      key: 'document',
    },
    {
      title: 'Correo Electrónico',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Teléfono',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Dirección',
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: 'Cargo',
      dataIndex: 'jobPosition',
      key: 'jobPosition',
    },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (id: number, record: any) => (
        <p style={{ color: record?.isActive ? '#209F35' : '#006AEA' }}>
          {record?.isActive ? 'Activo' : 'Inactivo'}
        </p>
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      align: 'right',
      width: 50,
      key: 'actions',
      render: (id: number, record: any) => (
        <ColumnItemWrapper floatingMenu>
          {/* <div className="text-gray-500 relative"> */}
          <FloatingMenu
            visible={menuVisible === id}
            onClose={() => {
              setMenuVisible(-1)
            }}
            options={{
              onEdit: async () => {
                let response = await axios.get(`${server}/employee?id=${id}`)
                setSelectedType('EDIT')
                  let birthDate = response?.data?.employee?.birthDate

                let hireDate = response?.data?.employee?.hireDate
                setSelectedEmployee({...response.data?.employee, birthDate: parseToDate(birthDate), hireDate: parseToDate(hireDate)})

                setShowModal(true)
              },
              onDelete: () => {
                setSelectedEmployeeToDelete(id)
              },
            }}
          >
            <HiDotsVertical
              className="cursor-pointer dark:text-white text-primaryGray-400"
              onClick={() => {
                setMenuVisible(id)
              }}
            />
          </FloatingMenu>
        </ColumnItemWrapper>
      ),
    },
  ]

  // Mostrar mensaje de upgrade si no es plan corporativo
  if (isNonCorporateUser) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-4">
        <div
          className="p-8 rounded-lg shadow-lg max-w-2xl text-center"
          style={{
            backgroundColor: theme === 'dark' ? '#1D1D1D' : '#FFFFFF',
            borderColor: theme === 'dark' ? '#2D2D2D' : '',
            borderWidth: theme === 'dark' ? 1 : 0,
          }}
        >
          <div className="mb-6">
            <MdPeople className="mx-auto mb-4" size={64} style={{ color: '#507CDC' }} />
          </div>
          <h2 className="text-2xl font-bold mb-4 dark:text-white">
            Funcionalidad Premium
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Para gestionar empleados debes mejorar tu plan a <strong className="text-primary">"Corporativo"</strong>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            El plan Corporativo incluye gestión completa de empleados con registro de información personal, historial laboral, documentos, beneficios, deducciones y mucho más.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <Table
          data={employees}
          columns={columns}
          pages={numberOfPages}
          pageSelected={currentPage}
          loading={loading}
          setPageSelected={(page) => {}}
          setLoading={setLoading}
        />
      </motion.div>

      <Modal
        display={showModal}
        onClose={() => {
          setShowModal(false)
          setSelectedEmployee(null)
        }}
      >
        <NewEmployeeForm
          onShowModal={(value) => {
            setSelectedBenefitDeduction(value)
          }}
          selectedEmployee={selectedEmployee}
          type={selectedType}
          onCancel={() => {
            setShowModal(false)
            setSelectedEmployee(null)
          }}
          onSubmit={async (values) => {
            if (selectedType === 'EDIT') {
              try {
                let response = await axios.patch(`${server}/employee`, {
                  ...values,
                  id: selectedEmployee?.id,
                })
                addAlert({
                  message: 'Empleado editado satisfactoriamente',
                  status: 'success',
                })

                setShowModal(false)
                onGetEmployees()
                setSelectedEmployee(null)
              } catch (err) {
                addAlert({
                  message: 'Error al editar empleado',
                  status: 'error',
                })
              }

              return
            }

            try {
              let response = await axios.post(`${server}/employee`, values)
              addAlert({
                message: 'Empleado creado satisfactoriamente',
                status: 'success',
              })

              setShowModal(false)
              onGetEmployees()
              setSelectedEmployee(null)
            } catch (err) {
              addAlert({
                message: 'Error al crear empleado',
                status: 'error',
              })
            }
          }}
        />
      </Modal>

      <Modal
        display={selectedBenefitDeduction !== null}
        onClose={() => {
          setSelectedBenefitDeduction(null)
        }}
      >
        <EditBenefitDeduction
          selectedBenefitDeduction={selectedBenefitDeduction}
          onCancel={() => {
            setSelectedBenefitDeduction(null)
          }}
          onSubmit={async (values) => {
            let selectedEmployeeCopy = { ...selectedEmployee }

            if (selectedBenefitDeduction?.state === 'BENEFIT') {
              if (selectedBenefitDeduction?.id) {
                try {
                  await axios.patch(`${server}/employee/benefit`, {
                    benefitId: selectedBenefitDeduction?.id,
                    description: values?.description,
                    amount: values?.amount,
                  })

                  addAlert({
                    message: 'Beneficio editado satisfactoriamente',
                    status: 'success',
                  })
                } catch (err) {
                  addAlert({
                    message: 'Error al editar beneficio',
                    status: 'error',
                  })
                  return
                }
              }

              console.log(
                'SELECTED BENEFIT DEDUCTION',
                selectedBenefitDeduction
              )

              console.log('SELECTED BENEFIT DEDUCTION 2', selectedEmployeeCopy)

              selectedEmployeeCopy.benefits[selectedBenefitDeduction?.idx] = {
                id: selectedBenefitDeduction?.id || null,
                description: values?.description,
                amount: values?.amount,
              }
            } else {
              if (selectedBenefitDeduction?.id) {
                try {
                  await axios.patch(`${server}/employee/deduction`, {
                    deductionId: selectedBenefitDeduction?.id,
                    description: values?.description,
                    amount: values?.amount,
                  })
                  addAlert({
                    message: 'Deducción editada satisfactoriamente',
                    status: 'success',
                  })
                } catch (err) {
                  addAlert({
                    message: 'Error al editar deducción',
                    status: 'error',
                  })
                  return
                }
              }

              selectedEmployeeCopy.deductions[selectedBenefitDeduction?.idx] = {
                id: selectedBenefitDeduction?.id,
                description: values?.description,
                amount: values?.amount,
              }
            }

            setSelectedEmployee(selectedEmployeeCopy)

            setSelectedBenefitDeduction(null)
          }}
        />
      </Modal>

      <ModalConfirm
        displayConfirmModal={selectedEmployeeToDelete !== null}
        title={'¿Está seguro que desea eliminar este empleado?'}
        onCancel={() => {
          setSelectedEmployeeToDelete(null)
        }}
        onConfirm={async () => {
          try {
            addAlert({
              message: 'Eliminando empleado...',
              status: 'loading',
            })
            await axios.delete(
              `${server}/employee?id=${selectedEmployeeToDelete}`
            )
            addAlert({
              message: 'Empleado eliminado satisfactoriamente',
              status: 'success',
            })
            await onGetEmployees()
            setSelectedEmployeeToDelete(null)
          } catch (err) {
            addAlert({
              message: 'Error al eliminar empleado',
              status: 'error',
            })
          }
        }}
      />
    </>
  )
}

interface IEmployee {
  setHeaderActions: React.Dispatch<
    React.SetStateAction<HeaderProps['headerActions'] | null | undefined>
  >
}

export default Employee
